import React, { useState, useRef } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  VolumeUp as VolumeUpIcon,
  ZoomIn as ZoomInIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'

const AnswerDetail = ({ questionData, onBack }) => {
  const { user } = useAuth()
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef(null)

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning'
      case 'answered':
        return 'success'
      case 'declined':
        return 'error'
      default:
        return 'default'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Bekliyor'
      case 'answered':
        return 'Cevaplandı'
      case 'declined':
        return 'Reddedildi'
      default:
        return 'Bilinmiyor'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'answered':
        return <CheckCircleIcon />
      case 'declined':
        return <CancelIcon />
      default:
        return <ScheduleIcon />
    }
  }

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const handlePlayVoice = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
        setIsPlaying(false)
      } else {
        audioRef.current.play()
        setIsPlaying(true)
      }
    }
  }

  const handleAudioEnded = () => {
    setIsPlaying(false)
  }

  const handleImageClick = () => {
    setImageDialogOpen(true)
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Soru Detayı
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Status Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Durum
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={getStatusText(questionData.status)}
                color={getStatusColor(questionData.status)}
                icon={getStatusIcon(questionData.status)}
                size="large"
              />
              <Typography variant="body2" color="text.secondary">
                {formatDate(questionData.sentDate)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Question Info Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Soru Bilgileri
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Öğretmen:</strong> {questionData.teacherName}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              {questionData.description}
            </Typography>
          </CardContent>
        </Card>

        {/* Question Image */}
        {questionData.imageUrl && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Soru Görseli
              </Typography>
              <Box
                component="img"
                src={questionData.imageUrl}
                alt="Soru görseli"
                sx={{
                  width: '100%',
                  maxHeight: 300,
                  objectFit: 'contain',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
                onClick={handleImageClick}
              />
              <Button
                startIcon={<ZoomInIcon />}
                onClick={handleImageClick}
                sx={{ mt: 1 }}
              >
                Görseli Büyüt
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Teacher Response */}
        {questionData.status === 'answered' && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" color="success.main" gutterBottom>
                Öğretmen Yanıtı
              </Typography>
              {questionData.teacherResponse && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {questionData.teacherResponse}
                </Typography>
              )}
              
              {/* Voice Response */}
              {questionData.teacherVoiceResponse && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={isPlaying ? <VolumeUpIcon /> : <PlayArrowIcon />}
                    onClick={handlePlayVoice}
                    color="info"
                  >
                    {isPlaying ? 'Durdur' : 'Sesli Yanıtı Dinle'}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Öğretmenin sesli yanıtı
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Decline Message */}
        {questionData.status === 'declined' && (
          <Card sx={{ mb: 3, backgroundColor: 'error.50' }}>
            <CardContent>
              <Typography variant="h6" color="error.main" gutterBottom>
                Soru Reddedildi
              </Typography>
              <Typography variant="body1" color="error.main">
                Öğretmeniniz bu soruyu yanıtlamayı reddetti. 
                Lütfen sorunuzu yeniden gözden geçirin veya başka bir öğretmene sorun.
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Hidden Audio Element */}
        {questionData.teacherVoiceResponse && (
          <audio
            ref={audioRef}
            src={questionData.teacherVoiceResponse}
            onEnded={handleAudioEnded}
            style={{ display: 'none' }}
          />
        )}
      </Container>

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Soru Görseli
        </DialogTitle>
        <DialogContent>
          <Box
            component="img"
            src={questionData.imageUrl}
            alt="Soru görseli"
            sx={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain'
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </SpaceTheme>
  )
}

export default AnswerDetail
